import React, { useMemo } from "react";
import { StackedLineChart } from "@mui/icons-material";
import { Feature, LineString, Point } from "geojson";
import { passing_draw_polygon as passingDrawMode } from "mapbox-gl-draw-passing-mode";

import { DRAWING_CONSTANTS } from "fond/constants";
import { inPole, inSpan } from "fond/layers";
import {
  DeleteButton,
  LineStringButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  UndoButton,
} from "fond/map/Toolbar";
import { PoleIcon } from "fond/svg_icons";
import { useAppSelector } from "fond/utils/hooks";

import DrawHandler from "../DrawHandler";
import directSelectAerial from "../modes/planner/aerial/directSelectAerial";
import drawAerialPoleMode from "../modes/planner/aerial/drawAerialPole";
import drawAerialSpanMode from "../modes/planner/aerial/drawAerialSpan";
import simpleSelectAerialMode from "../modes/planner/aerial/simpleSelectAerial";

import aerialDrawStyles from "./aerialDrawStyles";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [
      <PointButton key="drawPoint" title="Place a new Pole" icon={<PoleIcon />} />,
      <LineStringButton key="drawLineString" title="Place a new Span" icon={<StackedLineChart />} />,
    ],
  },
  {
    id: "snappingTools",
    type: "action",
    items: [<SnappingButton key="snap" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const modes = {
  draw_point: drawAerialPoleMode,
  draw_line_string: drawAerialSpanMode,
  simple_select: simpleSelectAerialMode,
  passing_draw_polygon: passingDrawMode,
  direct_select: directSelectAerial,
};

/**
 * This component is responsible for managing the drawing of spans and poles, as well as
 * handling the snapping of poles to spans. It uses the Mapbox Draw library to provide
 * drawing functionality and manages the state of the drawn features.
 */
const AerialHandler: React.FC = () => {
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);

  // The initial features are derived from the data layers for spans and poles.
  // The spans are drawn as line strings, while the poles are drawn as points.
  // The poles are also checked to see if they are snapped to any spans (collocated), and this
  // information is added to their properties for styling purposes.
  // Note: This is an expensive calculation, as it inspects every pole to determine
  // if it is snapped to a span or not.
  const initialFeatures = useMemo(() => {
    const spans: Feature<LineString>[] = (data?.layers[inSpan].features || []) as Feature<LineString>[];
    const poles: Feature<Point>[] = (data?.layers[inPole].features || []) as Feature<Point>[];

    // Flatten all span coordinates into a single array for faster lookup
    const spanCoordinates = new Set(spans.flatMap((span) => span.geometry.coordinates.map((c) => c.toString())));

    poles.forEach((pole) => {
      const poleCoord = pole.geometry.coordinates.toString();
      const hasConnections = spanCoordinates.has(poleCoord);

      pole.properties = { ...pole.properties, [DRAWING_CONSTANTS.Unsnapped]: (!hasConnections).toString() };
    });

    return [...spans, ...poles];
  }, [data]);

  const sources = useMemo(() => [{ source: inSpan }, { source: inPole }], []);

  return <DrawHandler initialFeatures={initialFeatures} sources={sources} styles={aerialDrawStyles} config={toolbarConfig} modes={modes} />;
};

export default AerialHandler;
